import React, { useContext, useMemo, useState } from 'react';
import { ResultsContext } from '../contexts/ResultsContext';
import { ThemeContext } from '../contexts/ThemeContext';
import { filterIgnoredGames } from '../utils/ignoredGames';
import './BestOfTheBestView.css';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import StarsIcon from '@mui/icons-material/Stars';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const BestOfTheBestView = ({ tournamentId, username }) => {
  const { results, loading, error } = useContext(ResultsContext);
  const { theme } = useContext(ThemeContext);
  
  const [sortConfig, setSortConfig] = useState(null);
  const [topN, setTopN] = useState(10);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig && sortConfig.key === key) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc';
      } else {
        // Cycle back to default sort
        setSortConfig(null);
        return;
      }
    }
    setSortConfig({ key, direction });
  };

  const bestData = useMemo(() => {
    if (!results || !results.games) return null;

    const games = filterIgnoredGames(results.games, tournamentId);
    
    const grandChampions = {}; // { playerName: [games] }
    const playerStats = {}; // { playerName: { topNRanks: [], allRanks: [] } }

    games.forEach(game => {
      if (!game.leaderboard) return;

      game.leaderboard.forEach(entry => {
        const pName = entry.userName;
        const rank = entry.rank;

        if (!playerStats[pName]) {
          playerStats[pName] = { topNRanks: [], allRanks: [], initials: entry.initials || '' };
        }
        
        playerStats[pName].allRanks.push(rank);
        if (rank <= topN) {
          playerStats[pName].topNRanks.push(rank);
        }

        if (rank === 1) {
          if (!grandChampions[pName]) {
            grandChampions[pName] = { games: [], initials: entry.initials || '' };
          }
          grandChampions[pName].games.push(game.title);
        }
      });
    });

    // Process Top N Performance
    let topNPerformers = Object.entries(playerStats)
      .filter(([_, stats]) => stats.topNRanks.length > 0)
      .map(([name, stats]) => {
        const topNAvg = stats.topNRanks.reduce((a, b) => a + b, 0) / stats.topNRanks.length;
        const overallAvg = stats.allRanks.reduce((a, b) => a + b, 0) / stats.allRanks.length;
        return {
          name,
          initials: stats.initials,
          topNCount: stats.topNRanks.length,
          topNAvg,
          overallAvg,
          gamesPlayed: stats.allRanks.length
        };
      });

    // Apply sorting
    if (sortConfig && sortConfig.key) {
      topNPerformers.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Map UI keys to data keys if they differ
        if (sortConfig.key === 'topCount') {
          aVal = a.topNCount;
          bVal = b.topNCount;
        }
        if (sortConfig.key === 'topAvg') {
          aVal = a.topNAvg;
          bVal = b.topNAvg;
        }

        // For name, do localeCompare
        if (sortConfig.key === 'name') {
          return sortConfig.direction === 'asc' 
            ? aVal.localeCompare(bVal) 
            : bVal.localeCompare(aVal);
        }

        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      });
    } else {
      // Default multi-level sort: 
      // 1. Top N count DESC
      // 2. Top N Avg ASC
      // 3. Overall Avg ASC
      topNPerformers.sort((a, b) => {
        if (b.topNCount !== a.topNCount) {
          return b.topNCount - a.topNCount;
        }
        if (a.topNAvg !== b.topNAvg) {
          return a.topNAvg - b.topNAvg;
        }
        return a.overallAvg - b.overallAvg;
      });
    }

    // Sort Grand Champions by number of titles
    const sortedGrandChampions = Object.entries(grandChampions)
      .map(([name, data]) => ({
        name,
        initials: data.initials,
        games: data.games
      }))
      .sort((a, b) => b.games.length - a.games.length);

    return { grandChampions: sortedGrandChampions, topNPerformers };
  }, [results, tournamentId, sortConfig, topN]);

  if (loading) return <div className="loading">Analyzing greatness...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!bestData) return null;

  const getSortIcon = (key) => {
    if (!sortConfig || sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? 
      <ArrowUpwardIcon className="sort-icon" /> : 
      <ArrowDownwardIcon className="sort-icon" />;
  };

  return (
    <div className={`best-of-best-view ${theme}`}>
      <div className="view-header">
        <div className="title-section">
          <StarsIcon className="header-icon" />
          <h2>Best of the Best</h2>
        </div>
        <div className="top-n-selector">
          <span className="selector-label">Show Top:</span>
          {[3, 5, 10, 20].map(n => (
            <button 
              key={n} 
              className={`top-n-btn ${topN === n ? 'active' : ''}`}
              onClick={() => setTopN(n)}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      <div className="best-cards-container">
        {/* Grand Champions Card */}
        <div className="best-card grand-champions-card">
          <div className="card-header">
            <EmojiEventsIcon className="card-icon gold" />
            <h3>Grand Champions</h3>
          </div>
          <div className="card-content">
            {bestData.grandChampions.length === 0 ? (
              <p className="no-data">No grand champions yet!</p>
            ) : (
              <div className="champions-list">
                {bestData.grandChampions.map(champ => (
                  <div key={champ.name} className="champion-item">
                    <div className="champion-info">
                      <span className="champion-name">{champ.name}</span>
                      {champ.initials && <span className="champion-initials">({champ.initials})</span>}
                      <span className="title-count">{champ.games.length} Title{champ.games.length > 1 ? 's' : ''}</span>
                    </div>
                    <div className="champion-games">
                      {champ.games.join(', ')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Top N Performance Card */}
        <div className="best-card top-n-performance-card">
          <div className="card-header">
            <LeaderboardIcon className="card-icon blue" />
            <h3>Top {topN} Elite</h3>
          </div>
          <div className="card-content">
            <div className="table-responsive">
              <table className="best-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')} className="sortable-header">
                      Player {getSortIcon('name')}
                    </th>
                    <th onClick={() => handleSort('topCount')} className="sortable-header text-center">
                      Top {topN}s {getSortIcon('topCount')}
                    </th>
                    <th onClick={() => handleSort('topAvg')} className="sortable-header text-center">
                      Top {topN} Avg {getSortIcon('topAvg')}
                    </th>
                    <th onClick={() => handleSort('overallAvg')} className="sortable-header text-center">
                      Overall Avg {getSortIcon('overallAvg')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {bestData.topNPerformers.map(performer => (
                    <tr key={performer.name} className={username && performer.name.toLowerCase() === username.toLowerCase() ? 'highlighted-row' : ''}>
                      <td>
                        <div className="performer-name-cell">
                          <span className="performer-name">{performer.name}</span>
                          {performer.initials && <span className="performer-initials">{performer.initials}</span>}
                        </div>
                      </td>
                      <td className="text-center">{performer.topNCount}</td>
                      <td className="text-center font-bold">{performer.topNAvg.toFixed(1)}</td>
                      <td className="text-center">{performer.overallAvg.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BestOfTheBestView;
